import { useEffect, useState } from 'react';

import { Button, Form } from 'antd';
import { MasterUseCaseInterface } from 'application/usecases/MasterUseCase';
import { PropertyUseCaseInterface } from 'application/usecases/PropertyUseCase';
import { UserUseCaseInterface } from 'application/usecases/UserUseCase';
import dayjs from 'dayjs';
import { MasterSubCategory } from 'domain/entities/MasterEntity';
import { ListedPropertyAssessmentMain } from 'domain/entities/PropertyEntity';
import { MemberInfo } from 'domain/entities/UserEntity';
import { DetailPropertyComponent } from 'presentation/components/DetailProperty';
import { openErrorNoti, openSuccessNoti } from 'presentation/components/Notification';
import { PageTitle } from 'presentation/components/PageTitle';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppDispatch, useAppSelector } from 'presentation/redux/hooks';
import { PRIVATE_ROUTES } from 'presentation/router/CONSTANTS';
import { useNavigate, useParams } from 'react-router-dom';
import { MASTER_CATEGORY_LIST } from 'utilities/constants/COMMON';
import { DATE_FORMAT } from 'utilities/constants/DATE';
import { commonHelpers } from 'utilities/helpers';
import { MEMBER_TYPE_CORPORATION } from '../CU-003-01/index';
import { MEMBER_TYPE_PERSONAL } from '../CU-003-01/index';
import { Detail } from '../CU-031-01/components/Detail';
import { ListedPropertyRoomGetAssessmentDetail } from '../CU-033-01/models/Property';
import { AssessmentDetail } from './components/AssessmentDetail';
import { ConfirmModalComponent } from './components/ConfirmModal';
import { SalePrice } from './components/SalePrice';
import { DetailProperty } from './models/DetailProperty';
import { SELLER_CLASSIFICATION } from 'presentation/components/HeaderRightComponent';

interface props {
    propertyUseCase: PropertyUseCaseInterface;
    masterUseCase: MasterUseCaseInterface;
    userUseCase: UserUseCaseInterface;
}

const MASTER_CODES = [
    MASTER_CATEGORY_LIST.CLASSIFICATION_BATH_AND_TOILET,
    MASTER_CATEGORY_LIST.INDOOR_LAUNDRY_AREA_CLASSIFICATION,
    MASTER_CATEGORY_LIST.STATUS_CLASSIFICATION,
    MASTER_CATEGORY_LIST.ROOM_LAYOUT_DIV,
];

const CU03501Page = ({ propertyUseCase, masterUseCase, userUseCase }: props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const { propertyId, registeredId } = useParams();

    const [open, setOpen] = useState<boolean>(false);
    const [detailProperty, setDetailProperty] = useState<DetailProperty>();
    const [detailAssessment, setDetailAssessment] =
        useState<ListedPropertyRoomGetAssessmentDetail>();
    const [userBasicInfo, setUserBasicInfo] = useState<MemberInfo>();
    const storedInput = useAppSelector((state) => state.CU03101Slice.storedInput);
    const [options, setOptions] = useState<{
        toiletBath: MasterSubCategory[];
        laundryArea: MasterSubCategory[];
        status: MasterSubCategory[];
        roomLayoutDiv: MasterSubCategory[];
    }>({
        toiletBath: [],
        laundryArea: [],
        status: [],
        roomLayoutDiv: [],
    });

    const [hasYkyc, setHasYkyc] = useState<boolean>(false);

    useEffect(() => {
        (async () => {
            if (storedInput) form.setFieldsValue(storedInput);
            await Promise.all([
                getDetailProperty({ propertyId: Number(propertyId) }),
                getListedPropertyRoomGetAssessmentDetail({
                    listedPropertyId: Number(registeredId),
                }),
                initiateOptions(),
            ]);
        })();
    }, []);

    const getListedPropertyRoomGetAssessmentDetail = async (params: {
        listedPropertyId: number;
    }) => {
        try {
            dispatch(startFetching());
            const response = await propertyUseCase.getListedPropertyRoomGetAssessmentDetail(params);
            if (response.result) {
                setDetailAssessment(response.result);
                setDatatoForm(response.result);
            }
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const setDatatoForm = (initData: ListedPropertyRoomGetAssessmentDetail) => {
        if (!initData) return;
        const data = {
            saleDueDate: initData?.listedProperty?.saleDueDate
                ? dayjs(initData?.listedProperty?.saleDueDate)
                : null,
            campaignCode: initData?.listedProperty?.campaignCode,
        };
        form.setFieldsValue(data);
    };

    const getDetailProperty = async (params: { propertyId: number }) => {
        try {
            dispatch(startFetching());
            const response = await propertyUseCase.getDetailProperty(params);
            if (response.result) setDetailProperty(response.result);
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const initiateOptions = async () => {
        try {
            dispatch(startFetching());

            const options = await Promise.all([
                masterUseCase.getMasterSubCategoryGroupList({
                    categoryCodes: MASTER_CODES,
                }),
                userUseCase.getUserInfo(),
            ]);
            const masterOptions = options[0];
            setOptions({
                toiletBath: masterOptions[MASTER_CATEGORY_LIST.CLASSIFICATION_BATH_AND_TOILET],
                laundryArea: masterOptions[MASTER_CATEGORY_LIST.INDOOR_LAUNDRY_AREA_CLASSIFICATION],
                status: masterOptions[MASTER_CATEGORY_LIST.STATUS_CLASSIFICATION],
                roomLayoutDiv: masterOptions[MASTER_CATEGORY_LIST.ROOM_LAYOUT_DIV],
            });
            const userInfo: MemberInfo = options[1].result;
            const hasYkyc = !(
                userInfo?.memberType == MEMBER_TYPE_CORPORATION.toString() ||
                userInfo?.memberType == MEMBER_TYPE_PERSONAL.toString() &&
                userInfo?.kycResult == '10'
            );
            setHasYkyc(hasYkyc);
            setUserBasicInfo(options[1].result);
        } catch (error: any) {
            console.error(error);
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const submit = async (value: any) => {
        if (!registeredId) return;
        try {
            dispatch(startFetching());
            const payload: ListedPropertyAssessmentMain = {
                id: Number(registeredId),
                saleDueDate: convertDate(value.saleDueDate),
                campaignCode: value.campaignCode,
            };
            const response = await propertyUseCase.putListedPropertyAssessmentMain(payload);
            if (response.status === '200') {
                openSuccessNoti();
                setOpen(true);
            }
        } catch (error: any) {
            console.error(error);

            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const backToPreviousStep = () => {
        navigate(
            `${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_033_01}/${propertyId}/${registeredId}`,
        );
    };

    const backToDashboard = () => {
        navigate(`${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_002_02}/${SELLER_CLASSIFICATION}`);
    };

    const onOk = () => {
        navigate(`${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_004_00}`);
    };

    const convertDate = (id: string) => {
        const date = new Date();               
        if (id === '0'){ 
            date.setMonth(date.getMonth() + 3);
        } 
        else if (id === '1'){
            date.setMonth(date.getMonth() + 6);
        }
        else {
            date.setMonth(date.getMonth() + 12); 
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;

        return formattedDate;
    };

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexFlow: 'column',
            }}
            className="CU-035-01-container"
        >
            <PageTitle title="本査定" />
            <section
                className="width-full-screen height-full-screen"
                style={{
                    padding: '0px',
                    height: '100%',
                    overflow: 'auto',
                    flexGrow: 1,
                    display: 'flex',
                }}
            >
                {detailProperty ? (
                    <section style={{ display: 'inline-block' }}>
                        <Detail data={detailProperty} />
                        <div style={{ height: 12 }}></div>
                        <DetailPropertyComponent
                            link={`${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_036_02}/${propertyId}/${registeredId}`}
                        />
                    </section>
                ) : null}
                <Form
                    style={{
                        width: 343,
                        height: 'fit-content',
                        margin: '0px 0px 0px 69px',
                        padding: 0,
                    }}
                    form={form}
                    onFinish={submit}
                >
                    <div className="label-form">売却希望価格</div>
                    <div
                        style={{ marginTop: 40, marginBottom: 40, width: '100%' }}
                        className="center-inside"
                    >
                        <div>
                            <span className="title-1 mr-8">
                                {commonHelpers
                                    .roundTenThousandYen(
                                        detailAssessment?.listedProperty?.askingSalePrice,
                                    )
                                    .toLocaleString('en-US', {
                                        maximumFractionDigits: 2,
                                    })}
                            </span>
                            <span className="title-1-unit">万円</span>
                        </div>
                    </div>
                    <section>
                        <SalePrice form={form} />
                        <AssessmentDetail data={detailAssessment} options={options} />
                    </section>

                    <section
                        style={{
                            width: '100%',
                            justifyContent: 'space-between',
                            display: 'flex',
                        }}
                    >
                        <Button
                            className="button-login-text"
                            style={{
                                width: 165.5,
                                height: 56,
                                marginTop: 16,
                                color: '#28323C',
                            }}
                            onClick={backToPreviousStep}
                        >
                            戻る
                        </Button>
                        <Button
                            type="primary"
                            className="button-login-text"
                            style={{ width: 165.5, height: 56, marginTop: 16 }}
                            htmlType="submit"
                        >
                            査定依頼へ
                        </Button>
                    </section>
                </Form>
                <ConfirmModalComponent
                    onCancel={backToDashboard}
                    onOk={hasYkyc ? onOk : undefined}
                    open={open}
                    description={
                        hasYkyc
                            ? 'ご依頼ありがとうございます。\n査定完了まで今しばらくお待ちください。\nなお、査定結果の確認には本人確認が必要です。'
                            : ''
                    }
                />
            </section>
        </div>
    );
};
export default CU03501Page;
